import {
  KIDS_BELTS, ADULT_BELTS, CHAMPION_THEME,
  calcOVR, calcBeltLevel, calcLevelProgress, sessionsToNextLevel,
  SKINS, HAIRS, GIS
} from './constants'

export function getBelt(fighter) {
  const belts = fighter.type === 'kids' ? KIDS_BELTS : ADULT_BELTS
  return belts[Math.min(fighter.belt_index || 0, belts.length - 1)]
}

export function getBeltTheme(fighter) {
  const belt = getBelt(fighter)
  return { bg: belt.bg, border: belt.border, color: belt.color }
}

export function isChampion(fighter, competitions) {
  if (!competitions) return false
  const fc = competitions.filter ? competitions.filter(c => c.fighter_id === fighter.id) : competitions
  return fc.filter(c => c.medal === 'gold').length >= 2
}

export function getTheme(fighter, competitions) {
  if (isChampion(fighter, competitions)) return CHAMPION_THEME
  return getBeltTheme(fighter)
}

export function calcRating(fighter, skills, competitions) {
  return calcOVR(fighter, skills, competitions)
}

export function calcCatScore(skills, catTechniqueIds) {
  if (!skills || !catTechniqueIds || catTechniqueIds.length === 0) return 0
  const relevant = skills.filter(s => catTechniqueIds.includes(s.technique_id))
  if (!relevant.length) return 0
  const avg = relevant.reduce((sum, s) => sum + (s.stars || 0), 0) / relevant.length
  // Stars 0-10, convert to 0-99 score within belt range
  return Math.min(99, Math.round(avg * 9.9))
}


// Adult belts get 4 stripes at levels 3, 5, 7, 9. Kids belts have no stripes (use level system).
export function getStripeCount(fighter, level) {
  if (fighter.type === 'kids') return 0
  if (level >= 9) return 4
  if (level >= 7) return 3
  if (level >= 5) return 2
  if (level >= 3) return 1
  return 0
}

export function getBeltLevel(fighter) {
  const belt = getBelt(fighter)
  return calcBeltLevel(belt, fighter.belt_sessions || 0)
}

export function getBeltLevelProgress(fighter) {
  const belt = getBelt(fighter)
  return calcLevelProgress(belt, fighter.belt_sessions || 0)
}

export function getSessionsToNextLevel(fighter) {
  const belt = getBelt(fighter)
  return sessionsToNextLevel(belt, fighter.belt_sessions || 0)
}

export function makeAvatarSvg(seed, a1, a2, size) {
  const sk = SKINS[seed % SKINS.length]
  const hr = HAIRS[seed % HAIRS.length]
  const gi = GIS[seed % GIS.length]
  const s = size
  const id = `av${seed}s${size}`
  return `<svg width="${s}" height="${s}" viewBox="0 0 ${s} ${s}" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="bg${id}" x1="0" y1="0" x2="0.5" y2="1"><stop offset="0%" stop-color="${a1}"/><stop offset="100%" stop-color="${a2}"/></linearGradient></defs>
    <rect width="${s}" height="${s}" fill="url(#bg${id})"/>
    <ellipse cx="${s*.5}" cy="${s*.88}" rx="${s*.48}" ry="${s*.28}" fill="${gi}"/>
    <line x1="${s*.5}" y1="${s*.72}" x2="${s*.5}" y2="${s}" stroke="rgba(255,255,255,0.15)" stroke-width="${s*.03}"/>
    <circle cx="${s*.5}" cy="${s*.42}" r="${s*.24}" fill="${sk}"/>
    <ellipse cx="${s*.5}" cy="${s*.27}" rx="${s*.25}" ry="${s*.17}" fill="${hr}"/>
    <ellipse cx="${s*.5}" cy="${s*.2}" rx="${s*.18}" ry="${s*.1}" fill="${hr}"/>
    <circle cx="${s*.5-s*.09}" cy="${s*.43}" r="${s*.033}" fill="rgba(25,12,5,.7)"/>
    <circle cx="${s*.5+s*.09}" cy="${s*.43}" r="${s*.033}" fill="rgba(25,12,5,.7)"/>
    <path d="M${s*.43} ${s*.52} Q${s*.5} ${s*.58} ${s*.57} ${s*.52}" stroke="rgba(0,0,0,.3)" stroke-width="${s*.022}" fill="none" stroke-linecap="round"/>
  </svg>`
}

export function makeInitialsSvg(first, last, a1, a2, size) {
  const ini = ((first || '?')[0] + (last || '?')[0]).toUpperCase()
  const id = `ini${ini}${size}`
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="ibg${id}" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="${a1}"/><stop offset="100%" stop-color="${a2}"/></linearGradient></defs>
    <rect width="${size}" height="${size}" fill="url(#ibg${id})"/>
    <text x="${size*.5}" y="${size*.62}" text-anchor="middle" font-size="${size*.36}" font-weight="500" fill="rgba(255,255,255,0.85)" font-family="sans-serif">${ini}</text>
  </svg>`
}

export function getFlagSvg(code) {
  const flags = {
    HR: `<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">
  <rect width="60" height="13.33" fill="#E1000F"/>
  <rect y="13.33" width="60" height="13.33" fill="#FFFFFF"/>
  <rect y="26.66" width="60" height="13.34" fill="#000091"/>
  <g transform="translate(24, 6)">
    <rect width="12" height="14" rx="0" ry="0" fill="#FFFFFF" stroke="#E1000F" stroke-width="0.5"/>
    <rect x="0" y="0" width="2" height="2" fill="#E1000F"/>
    <rect x="2" y="0" width="2" height="2" fill="#FFFFFF"/>
    <rect x="4" y="0" width="2" height="2" fill="#E1000F"/>
    <rect x="6" y="0" width="2" height="2" fill="#FFFFFF"/>
    <rect x="8" y="0" width="2" height="2" fill="#E1000F"/>
    <rect x="10" y="0" width="2" height="2" fill="#FFFFFF"/>
    <rect x="0" y="2" width="2" height="2" fill="#FFFFFF"/>
    <rect x="2" y="2" width="2" height="2" fill="#E1000F"/>
    <rect x="4" y="2" width="2" height="2" fill="#FFFFFF"/>
    <rect x="6" y="2" width="2" height="2" fill="#E1000F"/>
    <rect x="8" y="2" width="2" height="2" fill="#FFFFFF"/>
    <rect x="10" y="2" width="2" height="2" fill="#E1000F"/>
    <rect x="0" y="4" width="2" height="2" fill="#E1000F"/>
    <rect x="2" y="4" width="2" height="2" fill="#FFFFFF"/>
    <rect x="4" y="4" width="2" height="2" fill="#E1000F"/>
    <rect x="6" y="4" width="2" height="2" fill="#FFFFFF"/>
    <rect x="8" y="4" width="2" height="2" fill="#E1000F"/>
    <rect x="10" y="4" width="2" height="2" fill="#FFFFFF"/>
    <rect x="0" y="6" width="2" height="2" fill="#FFFFFF"/>
    <rect x="2" y="6" width="2" height="2" fill="#E1000F"/>
    <rect x="4" y="6" width="2" height="2" fill="#FFFFFF"/>
    <rect x="6" y="6" width="2" height="2" fill="#E1000F"/>
    <rect x="8" y="6" width="2" height="2" fill="#FFFFFF"/>
    <rect x="10" y="6" width="2" height="2" fill="#E1000F"/>
    <rect x="0" y="8" width="2" height="2" fill="#E1000F"/>
    <rect x="2" y="8" width="2" height="2" fill="#FFFFFF"/>
    <rect x="4" y="8" width="2" height="2" fill="#E1000F"/>
    <rect x="6" y="8" width="2" height="2" fill="#FFFFFF"/>
    <rect x="8" y="8" width="2" height="2" fill="#E1000F"/>
    <rect x="10" y="8" width="2" height="2" fill="#FFFFFF"/>
    <rect x="0" y="10" width="2" height="2" fill="#FFFFFF"/>
    <rect x="2" y="10" width="2" height="2" fill="#E1000F"/>
    <rect x="4" y="10" width="2" height="2" fill="#FFFFFF"/>
    <rect x="6" y="10" width="2" height="2" fill="#E1000F"/>
    <rect x="8" y="10" width="2" height="2" fill="#FFFFFF"/>
    <rect x="10" y="10" width="2" height="2" fill="#E1000F"/>
    <rect x="0" y="12" width="2" height="2" fill="#E1000F"/>
    <rect x="2" y="12" width="2" height="2" fill="#FFFFFF"/>
    <rect x="4" y="12" width="2" height="2" fill="#E1000F"/>
    <rect x="6" y="12" width="2" height="2" fill="#FFFFFF"/>
    <rect x="8" y="12" width="2" height="2" fill="#E1000F"/>
    <rect x="10" y="12" width="2" height="2" fill="#FFFFFF"/>
  </g>
</svg>`,
    IE: `<svg viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg"><rect width="1" height="2" fill="#169b62"/><rect x="1" width="1" height="2" fill="#fff"/><rect x="2" width="1" height="2" fill="#ff883e"/></svg>`,
    BR: `<svg viewBox="0 0 4 3" xmlns="http://www.w3.org/2000/svg"><rect width="4" height="3" fill="#009c3b"/><polygon points="2,0.3 3.7,1.5 2,2.7 0.3,1.5" fill="#fedf00"/><circle cx="2" cy="1.5" r="0.65" fill="#002776"/></svg>`,
    US: `<svg viewBox="0 0 7 4" xmlns="http://www.w3.org/2000/svg"><rect width="7" height="4" fill="#fff"/><rect width="7" height="0.62" fill="#b22234"/><rect y="1.23" width="7" height="0.62" fill="#b22234"/><rect y="2.46" width="7" height="0.62" fill="#b22234"/><rect y="3.38" width="7" height="0.62" fill="#b22234"/><rect width="2.8" height="2.15" fill="#3c3b6e"/></svg>`,
    PT: `<svg viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg"><rect width="1" height="2" fill="#006600"/><rect x="1" width="2" height="2" fill="#ff0000"/></svg>`,
    DE: `<svg viewBox="0 0 5 3" xmlns="http://www.w3.org/2000/svg"><rect width="5" height="1" fill="#000"/><rect y="1" width="5" height="1" fill="#dd0000"/><rect y="2" width="5" height="1" fill="#ffce00"/></svg>`,
    GB: `<svg viewBox="0 0 6 3" xmlns="http://www.w3.org/2000/svg"><rect width="6" height="3" fill="#012169"/><path d="M0,0 L6,3 M6,0 L0,3" stroke="#fff" stroke-width="1"/><path d="M3,0 V3 M0,1.5 H6" stroke="#fff" stroke-width="1.2"/><path d="M3,0 V3 M0,1.5 H6" stroke="#c8102e" stroke-width="0.7"/></svg>`,
    JP: `<svg viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg"><rect width="3" height="2" fill="#fff"/><circle cx="1.5" cy="1" r="0.55" fill="#bc002d"/></svg>`,
    AU: `<svg viewBox="0 0 4 2" xmlns="http://www.w3.org/2000/svg"><rect width="4" height="2" fill="#00008b"/><rect width="2" height="1" fill="#012169"/><path d="M0,0 L2,1 M2,0 L0,1" stroke="#fff" stroke-width="0.4"/><path d="M1,0 V1 M0,0.5 H2" stroke="#fff" stroke-width="0.5"/><path d="M1,0 V1 M0,0.5 H2" stroke="#c8102e" stroke-width="0.28"/></svg>`,
    FR: `<svg viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg"><rect width="1" height="2" fill="#002395"/><rect x="1" width="1" height="2" fill="#fff"/><rect x="2" width="1" height="2" fill="#ed2939"/></svg>`,
    ES: `<svg viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg"><rect width="3" height="2" fill="#c60b1e"/><rect y="0.5" width="3" height="1" fill="#ffc400"/></svg>`,
    IT: `<svg viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg"><rect width="1" height="2" fill="#009246"/><rect x="1" width="1" height="2" fill="#fff"/><rect x="2" width="1" height="2" fill="#ce2b37"/></svg>`,
  }
  return flags[code] || flags['HR']
}

export function todayDay() {
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
  return days[new Date().getDay()]
}

export function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
